// A paged list the backend serves as a bare array plus `total` and `per-page`
// response headers. The headers are read here and nowhere else.

export type Page<T> = {
  items: T[];
  total: number;
  // Null when the backend sent no page size.
  perPage: number | null;
};

// Axios hands back an AxiosHeaders instance in the browser and a plain object
// from a custom adapter; both are read case-insensitively.
export const readHeader = (headers: unknown, name: string): string | null => {
  if (typeof headers !== "object" || headers === null) return null;
  const wanted = name.toLowerCase();
  const withGet = headers as { get?: (key: string) => unknown };
  const value =
    typeof withGet.get === "function"
      ? withGet.get(name)
      : Object.entries(headers as Record<string, unknown>).find(
          ([key]) => key.toLowerCase() === wanted,
        )?.[1];
  return value === undefined || value === null ? null : String(value);
};

const readCount = (headers: unknown, name: string): number | null => {
  const value = readHeader(headers, name);
  if (value === null) return null;
  const count = parseInt(value, 10);
  return Number.isFinite(count) ? count : null;
};

export const readPage = <T>({
  headers,
  data,
}: {
  headers: unknown;
  data: T[] | null | undefined;
}): Page<T> => {
  const items = Array.isArray(data) ? data : [];
  return {
    items,
    // A list without a total is complete.
    total: readCount(headers, "total") ?? items.length,
    perPage: readCount(headers, "per-page"),
  };
};
